// Watchers for the Email intake page. Intake collections are written only by
// Cloud Functions; owners read them and act on review cards via a callable.
// Design: docs/ferrari-intake-design.md §8–§9.
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";

const DRY_RUN_ID = "m1-order-notices";

const millis = (ts) => (ts?.toMillis ? ts.toMillis() : 0);
const newestFirst = (field) => (a, b) => millis(b[field]) - millis(a[field]);

function watchCompanyCollection(path, companyId, field, callback, onError) {
  const q = query(collection(db, ...path), where("companyId", "==", companyId));
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(newestFirst(field))),
    onError,
  );
}

export function watchIntakeEmails(companyId, callback, onError) {
  return watchCompanyCollection(["intakeEmails"], companyId, "receivedAt", callback, onError);
}

export function watchIntakeReviews(companyId, callback, onError) {
  return watchCompanyCollection(["intakeReview"], companyId, "createdAt", callback, onError);
}

// runId: "m1-order-notices" (default), "m2-scans" or "m3-pods".
export function watchDryRunRows(companyId, callback, onError, runId = DRY_RUN_ID) {
  return watchCompanyCollection(["intakeDryRuns", runId, "rows"], companyId, "createdAt", callback, onError);
}

// The kill switch and live flags live on the company doc.
export function watchIntakeSettings(companyId, callback) {
  return onSnapshot(doc(db, "companies", companyId), (snap) => callback(snap.data()?.intake || null));
}

// Owner action on a review card: "dismiss" | "accept" | "use_email_date" |
// "set_date" (value = "YYYY-MM-DD") | "confirm_complete" (POD cards).
// See functions/intake/resolve.js.
export async function resolveIntakeReview(reviewId, action, value) {
  const fn = httpsCallable(functions, "resolveIntakeReview");
  const res = await fn({ reviewId, action, value });
  return res.data;
}

// Combine same-day jobs that were one run. jobIds[0] is the job that stays;
// the others are cancelled with a pointer to it. See functions/intake/combine.js.
export async function combineJobs(jobIds, asRoundtrip = false) {
  const fn = httpsCallable(functions, "combineJobs");
  const res = await fn({ jobIds, asRoundtrip });
  return res.data;
}
